import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Gift, List, Monitor, Shield, Users, User } from 'react-feather';
import MyCouponLogo from '../../../assets/images/MyCouponLogo.svg';
import MyCouponLogoMobile from '../../../assets/images/MyCouponLogoMobile.svg';
import AccountIcon from '../../../assets/icons/AccountIcon.svg';
import LogoutIcon from '../../../assets/icons/LogoutIcon.svg';
import { actions as authActions } from 'coupon-common/src/modules/auth';
import '../Components/NavbarSettingsDropdown.css';
import ImpersonationSwitcher from './ImpersonationSwitcher';

/* ── Nav icons — line-art SVGs matching the pill design ── */

const HomeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9.15 1.86a1.35 1.35 0 0 1 1.7 0l6.53 5.28c.4.33.62.81.62 1.32v8.04c0 .94-.76 1.7-1.7 1.7h-4.15a.85.85 0 0 1-.85-.85v-4.5c0-.4-.05-.75-.13-.93a1.55 1.55 0 0 0-1.17-.94 1.9 1.9 0 0 0-.7 0 1.55 1.55 0 0 0-1.17.94c-.08.18-.13.53-.13.93v4.5c0 .47-.38.85-.85.85H3.3c-.94 0-1.7-.76-1.7-1.7V8.46c0-.51.22-.99.62-1.32l6.93-5.28Z"
      fill="currentColor"
    />
  </svg>
);

const GridIcon = () => (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="5.2" cy="5.2" r="3.9" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="5.2" cy="14.8" r="3.9" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="14.8" cy="5.2" r="3.9" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="14.8" cy="14.8" r="3.9" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const TicketIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.5 7.5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v1.2a1.7 1.7 0 0 0 0 3.3v1.2a2 2 0 0 1-2 2h-11a2 2 0 0 1-2-2v-1.2a1.7 1.7 0 0 0 0-3.3V7.5Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path d="M8 6v1.4M8 9.3v1.4M8 12.6V14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="7.3" stroke="currentColor" strokeWidth="1.4" />
    <path d="M6.8 10.2l2.1 2.1 4.3-4.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PersonIcon = () => (
  <svg width="15" height="15" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="9" cy="5.4" r="4" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M1.6 18.4c0-4.42 3.3-8 7.4-8s7.4 3.58 7.4 8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

/* ── Top pill nav — kept to the app's primary destinations. Less
   frequent pages live in the Settings dropdown below. ── */
const NAV_ITEMS = [
  // { label: 'Home', to: '/', Icon: HomeIcon, end: true },
  { label: 'Dashboard', to: '/dashboard', Icon: GridIcon },
  { label: 'Profile', to: '/coupons', Icon: PersonIcon },
  // { label: 'Used Coupons', to: '/used-coupons', Icon: CheckCircleIcon },
  { label: 'Account', to: '/account', Icon: PersonIcon },
  { label: 'Blog', to: '/business-blogs', Icon: TicketIcon },
];

const ADMIN_EMAILS = ['info@mycouponbook.ca', 'charles.anthony.cruz02@gmail.com'];

console.log("[NavBar] rendering");

const NavBar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const businessEmail = useSelector(state => state.businessInfo?.data?.email);
  const isAdmin = ADMIN_EMAILS.includes(businessEmail ?? '');

  const isActive = to => {
    if (to === '/') return location.pathname === '/';
    const current = location.pathname.toLowerCase();
    const target = to.toLowerCase();
    return current === target || current.startsWith(`${target}/`);
  };

  // Close the settings dropdown when clicking outside of it
  useEffect(() => {
    const handleClickOutside = e => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAccount = () => {
    setSettingsOpen(false);
    navigate('/account');
  };

  const handleLogout = () => {
    setSettingsOpen(false);
    dispatch(authActions.logout());
  };

  const goTo = to => {
    setSettingsOpen(false);
    setMenuOpen(false);
    navigate(to);
  };

  return (
    <nav className="nav-root">
      <div className="nav-inner">

        {/* Logo — CSS controls which variant is visible */}
        <NavLink to="/dashboard" className="nav-logo-wrap">
          <img src={MyCouponLogo} className="nav-logo-desktop img-fluid" alt="MyCoupon" />
          <img src={MyCouponLogoMobile} className="nav-logo-mobile  img-fluid" alt="MyCoupon" />
        </NavLink>

        <div className="nav-right">
          {/* Desktop nav links — light pill bar, hidden below 1024px via CSS */}
          <div className="navbar-links nav-desktop-only">
            {NAV_ITEMS.map(({ label, to, Icon }) => (
              <NavLink
                key={label}
                to={to}
                className={`navbar-link${isActive(to) ? ' is-active' : ''}`}
              >
                <span className="navbar-link-icon"><Icon /></span>
                <span className="navbar-link-text">{label}</span>
              </NavLink>
            ))}
          </div>

          {/* Desktop-only controls — hidden below 1024px via CSS */}
          <button
            type="button"
            className="nav-create nav-desktop-only"
            onClick={() => navigate('/coupons/create')}
          >
            <span>🎟️</span> Create Coupon
          </button>

          {/* Settings — toggles a dropdown with the rest of the app's pages */}
          <div className="nav-settings-wrap nav-desktop-only" ref={settingsRef}>
            <button
              type="button"
              className={`nav-settings${settingsOpen ? ' active' : ''}`}
              onClick={() => setSettingsOpen(o => !o)}
              aria-haspopup="true"
              aria-expanded={settingsOpen}
            >
              ⚙️ Settings
            </button>

            {settingsOpen && (
              <div className="settings-dropdown" role="menu">
                <button type="button" className="settings-dropdown-item" role="menuitem" onClick={() => goTo('/membership-plans')}>
                  <Monitor className="settings-dropdown-icon" />
                  <span className="settings-dropdown-label">Membership Plans</span>
                </button>
                <button type="button" className="settings-dropdown-item" role="menuitem" onClick={() => goTo('/referral-program')}>
                  <Users className="settings-dropdown-icon" />
                  <span className="settings-dropdown-label">Referral Program</span>
                </button>
                <button type="button" className="settings-dropdown-item" role="menuitem" onClick={() => goTo('/points-requests')}>
                  <Gift className="settings-dropdown-icon" />
                  <span className="settings-dropdown-label">Points Requests</span>
                </button>
                <button type="button" className="settings-dropdown-item" role="menuitem" onClick={() => goTo('/points-transactions')}>
                  <List className="settings-dropdown-icon" />
                  <span className="settings-dropdown-label">Points Transactions</span>
                </button>
                {isAdmin && (
                  <button type="button" className="settings-dropdown-item" role="menuitem" onClick={() => goTo('/admin')}>
                    <Shield className="settings-dropdown-icon" />
                    <span className="settings-dropdown-label">Admin Panel</span>
                  </button>
                )}
                <button type="button" className="settings-dropdown-item" role="menuitem" onClick={handleAccount}>
                  <img src={AccountIcon} alt="" className="settings-dropdown-icon" />
                  <span className="settings-dropdown-label">Account</span>
                </button>
                <button type="button" className="settings-dropdown-item logout" role="menuitem" onClick={handleLogout}>
                  <img src={LogoutIcon} alt="" className="settings-dropdown-icon" />
                  <span className="settings-dropdown-label logout-label">Logout</span>
                </button>
              </div>
            )}
          </div>

          {/* Bell — always visible */}
          {/* <div className="nav-bell-wrap">
            <button type="button" className="nav-bell-btn">🔔</button>
            <span className="nav-bell-dot" />
          </div> */}

          {/* Hamburger — visible only on small/tablet via CSS */}
          <button
            type="button"
            className="nav-ham nav-mobile-only"
            onClick={() => setMenuOpen(o => !o)}
            aria-label="Toggle navigation menu"
            aria-expanded={menuOpen}
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>
      </div>

      {/* Mobile/tablet dropdown — toggled by JS state.
          Kept in parity with the desktop Settings dropdown: every
          destination reachable on desktop (Membership Plans, Referral
          Program, Points Requests, Points Transactions, Admin Panel,
          Account, Logout) is also reachable here, in addition to the
          primary NAV_ITEMS tabs. */}
      {menuOpen && (
        <div className="nav-mobile-menu">
          {NAV_ITEMS.map(({ label, to, Icon }) => (
            <NavLink
              key={label}
              to={to}
              className={`navbar-mobile-link${isActive(to) ? ' is-active' : ''}`}
              onClick={() => setMenuOpen(false)}
            >
              <span className="navbar-link-icon"><Icon /></span>
              <span>{label}</span>
            </NavLink>
          ))}
          <button type="button" className="navbar-mobile-link" onClick={() => goTo('/membership-plans')}>
            <span className="navbar-link-icon"><Monitor size={16} /></span>
            <span>Membership Plans</span>
          </button>
          {/* <button type="button" className="navbar-mobile-link" onClick={() => goTo('/referral-program')}>
            <span className="navbar-link-icon"><Users size={16} /></span>
            <span>Referral Program</span>
          </button> */}
          <button type="button" className="navbar-mobile-link" onClick={() => goTo('/points-requests')}>
            <span className="navbar-link-icon"><Gift size={16} /></span>
            <span>Points Requests</span>
          </button>
          <button type="button" className="navbar-mobile-link" onClick={() => goTo('/points-transactions')}>
            <span className="navbar-link-icon"><List size={16} /></span>
            <span>Points Transactions</span>
          </button>
          {isAdmin && (
            <button type="button" className="navbar-mobile-link" onClick={() => goTo('/admin')}>
              <span className="navbar-link-icon"><Shield size={16} /></span>
              <span>Admin Panel</span>
            </button>
          )}
          <button type="button" className="navbar-mobile-link" onClick={handleAccount}>
            <span className="navbar-link-icon"><User size={16} /></span>
            <span>Account</span>
          </button>
          <button type="button" className="nav-mobile-create" onClick={() => goTo('/coupons/create')}>
            <span>🎟️</span> Create Coupon
          </button>
          <button type="button" className="navbar-mobile-link logout" onClick={handleLogout}>
            <span>Logout</span>
          </button>
        </div>
      )}
      <ImpersonationSwitcher />
    </nav>
  );
};

export default NavBar;